// src/pages/Admin/PagesPersonnalisees/PagesPersonnalisees.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import Button from "./../../../components/Button/Button";
import PageList from "./PageList";
import CreatePageModal from "./CreatePageModal";
import EditPageModal from "./EditPageModal";
import ManageArticlesModal from "./ManageArticlesModal";
import { getAllPages, deletePage } from "./../../../services/pagesPersonnaliseesServices";
import { FaPlus, FaSearch, FaFileAlt } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

export default function PagesPersonnalisees() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pageToEdit, setPageToEdit] = useState(null);

  const [showArticlesModal, setShowArticlesModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);

  const loadPages = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllPages();
      setPages(Array.isArray(result) ? result : []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des pages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const handleDeletePage = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer cette page ?")) return;
    try {
      const ok = await deletePage(id);
      if (ok) {
        toast.success("Page supprimée avec succès");
        loadPages();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression");
    }
  };

  const filteredPages = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return pages;
    return pages.filter((p) =>
      `${p?.title || ""} ${p?.slug || ""} ${p?.description || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [pages, searchTerm]);

  return (
    <section className="w-full">
      {/* Offset fiable pour la navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        {/* Header page */}
                <HeaderTitle
                  role="Administrateur"
                  title="Pages Personnalisées"
                  align="center"
                  className="mb-1"
                />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
                  <FaFileAlt />
                </div>
                <div>
                  <p className="text-white text-lg sm:text-xl font-semibold">
                    Gestion des pages CMS
                  </p>
                  <p className="text-white/90 text-sm">
                    Créez, modifiez et organisez vos pages personnalisées. Associez des articles via “Gérer les articles”.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  text="Créer une nouvelle page"
                  icon={FaPlus}
                  onClick={() => setShowCreateModal(true)}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-1"
                />
              </div>
            </div>
          </div>

          {/* Barre recherche */}
          <div className="px-5 sm:px-8 pt-5">
            <div className="w-full sm:max-w-md">
              <label htmlFor="search-pages" className="sr-only">
                Rechercher une page
              </label>
              <div className="relative">
                <input
                  id="search-pages"
                  type="text"
                  placeholder="Rechercher par titre, chemin de la page ou description…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-5 sm:p-8">
            {/* Skeleton */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-20"
                  />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && filteredPages.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucune page trouvée
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajustez votre recherche ou créez une nouvelle page.
                </p>
              </div>
            )}

            {/* Liste (déportée dans votre composant PageList) */}
            {!loading && filteredPages.length > 0 && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-3 sm:p-4">
                <PageList
                  pages={filteredPages}
                  onEdit={(page) => {
                    setPageToEdit(page);
                    setShowEditModal(true);
                  }}
                  onDelete={handleDeletePage}
                  onManageArticles={(page) => {
                    setSelectedPage(page);
                    setShowArticlesModal(true);
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      {showCreateModal && (
        <CreatePageModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreateSuccess={() => {
            setShowCreateModal(false);
            loadPages();
          }}
        />
      )}

      {showEditModal && (
        <EditPageModal
          isOpen={showEditModal}
          page={pageToEdit}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            loadPages();
          }}
        />
      )}

      {showArticlesModal && (
        <ManageArticlesModal
          page={selectedPage}
          onClose={() => setShowArticlesModal(false)}
        />
      )}
    </section>
  );
}
